import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type {
  ContractGenerationContextInput,
  ContractGenerationPreviewRecord,
  ContractTemplateCollectionRecord,
  ContractTemplateDetailRecord,
  ContractTemplatePlaceholderRecord,
  ContractTemplateUpsertInput
} from '@shared/types';
import { useSearchStore } from '@renderer/app/state/search-store';
import { useShellStore } from '@renderer/app/state/shell-store';
import { CollectionTable } from '@renderer/shared/components/collection-table';
import { EditorBodyLayout, EditorHeroCard, EditorPageLayout, EditorSectionCard } from '@renderer/shared/components/editor-scaffold';
import { Icon } from '@renderer/shared/components/icons';
import {
  EditorTabs,
  EmptyState,
  OverflowMenuButton,
  SelectField,
  StatusBadge,
  TextAreaField,
  TextField,
  ValuePair
} from '@renderer/shared/components/primitives';
import { useI18n } from '@renderer/shared/i18n';
import { isTextEditingElement } from '@renderer/shared/utils/is-text-editing-element';
import { useRegisterEditorSaveHandler } from '@renderer/shared/hooks/use-register-editor-save-handler';
import {
  generateContractFromTemplate,
  getContractTemplateById,
  listContractTemplates,
  previewContractGeneration,
  saveContractTemplate
} from './contract-templates-repository';
import {
  createContractTemplateDraft,
  createEmptyContractTemplateDraft,
  createEmptyPlaceholder,
  createInitialGenerationContext,
  getPlaceholderLabel,
  isTemplateDraftDirty,
  selectManualPlaceholders
} from './contract-templates-selectors';
import { useContractTemplatesViewStore } from './contract-templates-view-store';

function toNullable(value: string): string | null {
  return value.trim().length > 0 ? value : null;
}

function formatDateInputValue(value: string | null): string {
  return value ? value.slice(0, 10) : '';
}

function toIsoDate(value: string): string | null {
  return value ? `${value}T12:00:00.000Z` : null;
}

function formatSnapshotAddress(value: ContractGenerationPreviewRecord['customerSnapshot']): string | null {
  return value?.billingAddress?.formatted ?? null;
}

function formatRepresentativeName(value: ContractGenerationPreviewRecord['customerSnapshot']): string | null {
  if (!value?.representative.name) {
    return null;
  }

  if (!value.representative.role) {
    return value.representative.name;
  }

  return `${value.representative.name} - ${value.representative.role}`;
}

export function ContractTemplateEditorScreen(): JSX.Element {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const { locale, t } = useI18n();
  const setRouteDetailTitle = useShellStore((state) => state.setRouteDetailTitle);
  const { editorTab, setEditorTab } = useContractTemplatesViewStore();
  const [collection, setCollection] = useState<ContractTemplateCollectionRecord | null>(null);
  const [detail, setDetail] = useState<ContractTemplateDetailRecord | null>(null);
  const [draft, setDraft] = useState<ContractTemplateUpsertInput | null>(null);
  const [generationContext, setGenerationContext] = useState<ContractGenerationContextInput | null>(null);
  const [preview, setPreview] = useState<ContractGenerationPreviewRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const isNewTemplate = templateId === 'new';

  useEffect(() => {
    if (!templateId) {
      setError('Missing contract template id.');
      return;
    }

    let active = true;

    if (isNewTemplate) {
      void listContractTemplates()
        .then((nextCollection) => {
          if (!active) {
            return;
          }

          setCollection(nextCollection);
          setDraft(createEmptyContractTemplateDraft(nextCollection.companyProfiles));
          setDetail(null);
          setGenerationContext(null);
          setPreview(null);
          setRouteDetailTitle(t('contracts.templates.newTitle'));
        })
        .catch((nextError) => {
          if (active) {
            setError(nextError instanceof Error ? nextError.message : 'Template editor unavailable.');
          }
        });
    } else {
      void getContractTemplateById(templateId)
        .then((nextDetail) => {
          if (!active) {
            return;
          }

          if (!nextDetail) {
            setError('Contract template not found.');
            return;
          }

          setDetail(nextDetail);
          setDraft(createContractTemplateDraft(nextDetail));
          setGenerationContext(createInitialGenerationContext(nextDetail));
          setPreview(null);
          setRouteDetailTitle(nextDetail.template.name);
        })
        .catch((nextError) => {
          if (active) {
            setError(nextError instanceof Error ? nextError.message : 'Template editor unavailable.');
          }
        });
    }

    return () => {
      active = false;
      setRouteDetailTitle(null);
    };
  }, [isNewTemplate, setRouteDetailTitle, templateId]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || isTextEditingElement(document.activeElement) || useSearchStore.getState().isOpen) {
        return;
      }

      navigate('/contracts/templates');
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  const companyProfiles = detail?.companyProfiles ?? collection?.companyProfiles ?? [];
  const customerOptions = detail?.customerSummaries ?? [];
  const projectOptions = detail?.projectSummaries.filter((project) => project.customerId === generationContext?.customerId) ?? [];
  const manualPlaceholders = useMemo(
    () => selectManualPlaceholders(draft?.version.placeholderSchema ?? []),
    [draft?.version.placeholderSchema]
  );
  const dirty = useMemo(() => isTemplateDraftDirty(draft ?? createEmptyContractTemplateDraft(companyProfiles), detail), [companyProfiles, detail, draft]);

  useRegisterEditorSaveHandler(
    draft
      ? () => {
          if (isSaving) {
            return;
          }

          if (!dirty) {
            return;
          }

          void handleSaveTemplate();
        }
      : null
  );

  if (error) {
    return (
      <div className="av-page">
        <EmptyState title={t('contracts.templates.title')} description={error} />
      </div>
    );
  }

  if (!draft) {
    return <div className="av-page" />;
  }

  function updateDraft(patch: Partial<ContractTemplateUpsertInput>): void {
    setDraft((current) => (current ? { ...current, ...patch } : current));
  }

  function updateVersionDraft(patch: Partial<ContractTemplateUpsertInput['version']>): void {
    setDraft((current) =>
      current
        ? {
            ...current,
            version: {
              ...current.version,
              ...patch
            }
          }
        : current
    );
  }

  function updatePlaceholder(index: number, patch: Partial<ContractTemplatePlaceholderRecord>): void {
    setDraft((current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        version: {
          ...current.version,
          placeholderSchema: current.version.placeholderSchema.map((placeholder, placeholderIndex) =>
            placeholderIndex === index ? { ...placeholder, ...patch } : placeholder
          )
        }
      };
    });
  }

  function removePlaceholder(index: number): void {
    setDraft((current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        version: {
          ...current.version,
          placeholderSchema: current.version.placeholderSchema.filter((_, placeholderIndex) => placeholderIndex !== index)
        }
      };
    });
  }

  function updateGenerationContext(patch: Partial<ContractGenerationContextInput>): void {
    setGenerationContext((current) => (current ? { ...current, ...patch } : current));
  }

  function updateContractValues(
    patch: Partial<ContractGenerationContextInput['contractValues']>
  ): void {
    setGenerationContext((current) =>
      current
        ? {
            ...current,
            contractValues: {
              ...current.contractValues,
              ...patch
            }
          }
        : current
    );
  }

  async function handleSaveTemplate(): Promise<void> {
    if (!draft) {
      return;
    }

    const nextDraft: ContractTemplateUpsertInput = {
      ...draft,
      notes: toNullable(draft.notes ?? ''),
      version: {
        ...draft.version,
        changeSummary: toNullable(draft.version.changeSummary ?? ''),
        outputRules: {
          numberingPrefix: toNullable(draft.version.outputRules.numberingPrefix ?? ''),
          signatureFooter: toNullable(draft.version.outputRules.signatureFooter ?? '')
        },
        placeholderSchema: draft.version.placeholderSchema.map((placeholder) => ({
          ...placeholder,
          fallbackNote: toNullable(placeholder.fallbackNote ?? '')
        }))
      }
    };

    setIsSaving(true);
    setError(null);
    setSaveMessage(null);

    try {
      const nextDetail = await saveContractTemplate(nextDraft);

      setDetail(nextDetail);
      setCollection(null);
      setDraft(createContractTemplateDraft(nextDetail));
      setGenerationContext((current) => current ?? createInitialGenerationContext(nextDetail));
      setSaveMessage(t('contracts.templates.messages.templateSaved'));

      if (isNewTemplate) {
        void navigate(`/contracts/templates/${nextDetail.template.id}`, { replace: true });
      }
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Template save failed.');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleRefreshPreview(): Promise<void> {
    if (!detail?.currentVersion || !generationContext) {
      return;
    }

    setIsPreviewing(true);
    setPreviewError(null);

    try {
      const nextPreview = await previewContractGeneration({
        templateId: detail.template.id,
        templateVersionId: detail.currentVersion.id,
        context: generationContext
      });

      setPreview(nextPreview);
    } catch (nextError) {
      setPreviewError(nextError instanceof Error ? nextError.message : 'Template preview failed.');
    } finally {
      setIsPreviewing(false);
    }
  }

  async function handleGenerateContract(): Promise<void> {
    if (!detail?.currentVersion || !generationContext) {
      return;
    }

    setIsGenerating(true);
    setPreviewError(null);

    try {
      const contractDetail = await generateContractFromTemplate({
        templateId: detail.template.id,
        templateVersionId: detail.currentVersion.id,
        context: generationContext
      });

      void navigate(`/contracts/${contractDetail.contract.id}`);
    } catch (nextError) {
      setPreviewError(nextError instanceof Error ? nextError.message : 'Contract generation failed.');
    } finally {
      setIsGenerating(false);
    }
  }

  return (
    <EditorPageLayout
      hero={
        <EditorHeroCard actions={<OverflowMenuButton />}>
          <div className="av-editor-hero-card__content">
            <div className="av-editor-hero-card__identity">
              <div className="av-editor-hero-card__icon">
                <Icon name="document" />
              </div>
              <div className="av-editor-hero-card__identity-copy">
                <h2>{draft.name || t('contracts.templates.newTitle')}</h2>
                <div className="av-editor-hero-card__meta">
                  <StatusBadge label={t(`enums.contractTemplateStatus.${draft.status}`)} tone={draft.status === 'active' ? 'success' : draft.status === 'archived' ? 'neutral' : 'warning'} />
                  <StatusBadge
                    label={
                      detail?.currentVersion
                        ? t('contracts.templates.versions.versionLabel', { version: detail.currentVersion.versionNumber })
                        : t('contracts.templates.versions.none')
                    }
                    tone="dark"
                  />
                </div>
              </div>
            </div>

            <div className="av-editor-hero-card__endpoints">
              <div className="av-endpoint-row">
                <Icon name="contracts" />
                <span>{t(`enums.contractTemplateCategory.${draft.category}`)}</span>
              </div>
              <div className="av-endpoint-row">
                <Icon name="settings" />
                <span>{companyProfiles.find((profile) => profile.id === draft.defaultIssuerCompanyProfileId)?.name ?? t('common.notSet')}</span>
              </div>
              <div className="av-endpoint-row">
                <Icon name="document" />
                <span>{t(`common.languages.${draft.language}`)}</span>
              </div>
            </div>
          </div>
        </EditorHeroCard>
      }
      heroAside={
        <EditorSectionCard title={t('contracts.templates.sections.summary')} actions={<OverflowMenuButton />}>
          <div className="av-summary-stack">
            <ValuePair label={t('contracts.templates.summaryFields.slug')} value={draft.slug || t('common.notSet')} />
            <ValuePair label={t('contracts.templates.summaryFields.placeholders')} value={draft.version.placeholderSchema.length} />
            <ValuePair label={t('contracts.templates.summaryFields.manualTokens')} value={manualPlaceholders.length} />
            <ValuePair
              label={t('contracts.templates.summaryFields.previewStatus')}
              value={
                detail?.currentVersion
                  ? preview
                    ? t('contracts.templates.preview.ready')
                    : t('contracts.templates.preview.pending')
                  : t('contracts.templates.preview.saveRequired')
              }
            />
          </div>
        </EditorSectionCard>
      }
      tabs={
        <EditorTabs
          tabs={[
            { id: 'general', label: t('contracts.templates.tabs.general'), icon: 'settings' },
            { id: 'body', label: t('contracts.templates.tabs.body'), icon: 'document' },
            { id: 'placeholders', label: t('contracts.templates.tabs.placeholders'), icon: 'list' },
            { id: 'outputRules', label: t('contracts.templates.tabs.outputRules'), icon: 'contracts' },
            { id: 'versions', label: t('contracts.templates.tabs.versions'), icon: 'timer' }
          ]}
          value={editorTab}
          onChange={setEditorTab}
        />
      }
    >
      <EditorBodyLayout
        main={
          <>
            {saveMessage ? <div className="av-inline-success">{saveMessage}</div> : null}

            {editorTab === 'general' ? (
              <EditorSectionCard
                title={t('contracts.templates.sections.general')}
                actions={
                  <button className="av-primary-button" disabled={isSaving} type="button" onClick={() => void handleSaveTemplate()}>
                    {t('contracts.templates.actions.saveTemplate')}
                  </button>
                }
              >
                <div className="av-form-grid">
                  <label className="av-form-field">
                    <span>{t('contracts.templates.fields.name')}</span>
                    <TextField value={draft.name} onValueChange={(value) => updateDraft({ name: value })} />
                  </label>
                  <label className="av-form-field">
                    <span>{t('contracts.templates.fields.slug')}</span>
                    <TextField value={draft.slug} onValueChange={(value) => updateDraft({ slug: value })} />
                  </label>
                  <label className="av-form-field">
                    <span>{t('contracts.templates.fields.category')}</span>
                    <SelectField
                      options={[
                        { value: 'service_agreement', label: t('enums.contractTemplateCategory.service_agreement') },
                        { value: 'website_development', label: t('enums.contractTemplateCategory.website_development') },
                        { value: 'maintenance', label: t('enums.contractTemplateCategory.maintenance') },
                        { value: 'marketing', label: t('enums.contractTemplateCategory.marketing') },
                        { value: 'hosting', label: t('enums.contractTemplateCategory.hosting') },
                        { value: 'consulting', label: t('enums.contractTemplateCategory.consulting') },
                        { value: 'nda', label: t('enums.contractTemplateCategory.nda') },
                        { value: 'annex', label: t('enums.contractTemplateCategory.annex') }
                      ]}
                      value={draft.category}
                      onChange={(event) => updateDraft({ category: event.target.value as ContractTemplateUpsertInput['category'] })}
                    />
                  </label>
                  <label className="av-form-field">
                    <span>{t('contracts.templates.fields.language')}</span>
                    <SelectField
                      options={[
                        { value: 'ro-RO', label: t('common.languages.ro-RO') },
                        { value: 'en-US', label: t('common.languages.en-US') }
                      ]}
                      value={draft.language}
                      onChange={(event) => updateDraft({ language: event.target.value as ContractTemplateUpsertInput['language'] })}
                    />
                  </label>
                  <label className="av-form-field">
                    <span>{t('contracts.templates.fields.status')}</span>
                    <SelectField
                      options={[
                        { value: 'draft', label: t('enums.contractTemplateStatus.draft') },
                        { value: 'active', label: t('enums.contractTemplateStatus.active') },
                        { value: 'archived', label: t('enums.contractTemplateStatus.archived') }
                      ]}
                      value={draft.status}
                      onChange={(event) => updateDraft({ status: event.target.value as ContractTemplateUpsertInput['status'] })}
                    />
                  </label>
                  <label className="av-form-field">
                    <span>{t('contracts.templates.fields.defaultIssuer')}</span>
                    <SelectField
                      options={[
                        { value: '', label: t('common.notSet') },
                        ...companyProfiles.map((profile) => ({
                          value: profile.id,
                          label: profile.name
                        }))
                      ]}
                      value={draft.defaultIssuerCompanyProfileId ?? ''}
                      onChange={(event) => updateDraft({ defaultIssuerCompanyProfileId: event.target.value || null })}
                    />
                  </label>
                </div>
                <div className="av-form-field">
                  <span>{t('contracts.templates.fields.notes')}</span>
                  <TextAreaField
                    rows={4}
                    value={draft.notes ?? ''}
                    onValueChange={(value) => updateDraft({ notes: value || null })}
                  />
                </div>
              </EditorSectionCard>
            ) : null}

            {editorTab === 'body' ? (
              <EditorSectionCard
                title={t('contracts.templates.sections.body')}
                actions={
                  <button className="av-primary-button" disabled={isSaving} type="button" onClick={() => void handleSaveTemplate()}>
                    {t('contracts.templates.actions.saveTemplate')}
                  </button>
                }
              >
                <div className="av-form-field">
                  <span>{t('contracts.templates.fields.bodySource')}</span>
                  <TextAreaField
                    rows={18}
                    value={draft.version.bodySource}
                    onValueChange={(value) => updateVersionDraft({ bodySource: value })}
                  />
                </div>
              </EditorSectionCard>
            ) : null}

            {editorTab === 'placeholders' ? (
              <EditorSectionCard
                title={t('contracts.templates.sections.placeholders')}
                actions={
                  <div className="av-toolbar-group">
                    <button
                      className="av-secondary-button"
                      type="button"
                      onClick={() =>
                        updateVersionDraft({
                          placeholderSchema: [...draft.version.placeholderSchema, createEmptyPlaceholder()]
                        })
                      }
                    >
                      {t('contracts.templates.actions.addPlaceholder')}
                    </button>
                    <button className="av-primary-button" disabled={isSaving} type="button" onClick={() => void handleSaveTemplate()}>
                      {t('contracts.templates.actions.saveTemplate')}
                    </button>
                  </div>
                }
              >
                {draft.version.placeholderSchema.length === 0 ? (
                  <EmptyState title={t('contracts.templates.sections.placeholders')} description={t('contracts.templates.empty.placeholders')} />
                ) : (
                  <CollectionTable
                    ariaLabel={t('contracts.templates.sections.placeholders')}
                    columns={[
                      { id: 'key', label: t('contracts.templates.placeholderColumns.key') },
                      { id: 'meta', label: t('contracts.templates.placeholderColumns.meta') },
                      { id: 'label', label: t('contracts.templates.placeholderColumns.label') },
                      { id: 'fallback', label: t('contracts.templates.placeholderColumns.fallback') },
                      { id: 'actions', label: '', align: 'right' }
                    ]}
                  >
                    {draft.version.placeholderSchema.map((placeholder, index) => (
                      <tr key={`${placeholder.key || 'placeholder'}-${index}`} className="av-collection-table__row">
                        <td className="av-collection-table__cell">
                          <div className="av-summary-stack">
                            <TextField value={placeholder.key} onValueChange={(value) => updatePlaceholder(index, { key: value })} />
                            <SelectField
                              options={[
                                { value: 'customer', label: t('contracts.templates.groups.customer') },
                                { value: 'issuer', label: t('contracts.templates.groups.issuer') },
                                { value: 'project', label: t('contracts.templates.groups.project') },
                                { value: 'contract', label: t('contracts.templates.groups.contract') },
                                { value: 'manual', label: t('contracts.templates.groups.manual') }
                              ]}
                              value={placeholder.group}
                              onChange={(event) =>
                                updatePlaceholder(index, {
                                  group: event.target.value as ContractTemplatePlaceholderRecord['group']
                                })
                              }
                            />
                          </div>
                        </td>
                        <td className="av-collection-table__cell">
                          <div className="av-summary-stack">
                            <SelectField
                              options={[
                                { value: 'customer', label: t('contracts.templates.sources.customer') },
                                { value: 'issuer', label: t('contracts.templates.sources.issuer') },
                                { value: 'project', label: t('contracts.templates.sources.project') },
                                { value: 'contract', label: t('contracts.templates.sources.contract') },
                                { value: 'manual', label: t('contracts.templates.sources.manual') }
                              ]}
                              value={placeholder.sourceKind}
                              onChange={(event) =>
                                updatePlaceholder(index, {
                                  sourceKind: event.target.value as ContractTemplatePlaceholderRecord['sourceKind']
                                })
                              }
                            />
                            <SelectField
                              options={[
                                { value: 'string', label: t('contracts.templates.valueTypes.string') },
                                { value: 'multiline', label: t('contracts.templates.valueTypes.multiline') },
                                { value: 'currency', label: t('contracts.templates.valueTypes.currency') },
                                { value: 'date', label: t('contracts.templates.valueTypes.date') }
                              ]}
                              value={placeholder.valueType}
                              onChange={(event) =>
                                updatePlaceholder(index, {
                                  valueType: event.target.value as ContractTemplatePlaceholderRecord['valueType']
                                })
                              }
                            />
                            <label className="av-checkbox-field">
                              <input
                                checked={placeholder.required}
                                type="checkbox"
                                onChange={(event) => updatePlaceholder(index, { required: event.target.checked })}
                              />
                              <span>{t('contracts.templates.fields.required')}</span>
                            </label>
                          </div>
                        </td>
                        <td className="av-collection-table__cell">
                          <TextField value={placeholder.labelKey} onValueChange={(value) => updatePlaceholder(index, { labelKey: value })} />
                        </td>
                        <td className="av-collection-table__cell">
                          <TextField value={placeholder.fallbackNote ?? ''} onValueChange={(value) => updatePlaceholder(index, { fallbackNote: value || null })} />
                        </td>
                        <td className="av-collection-table__cell is-right">
                          <button className="av-secondary-button" type="button" onClick={() => removePlaceholder(index)}>
                            {t('contracts.templates.actions.removePlaceholder')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </CollectionTable>
                )}
              </EditorSectionCard>
            ) : null}

            {editorTab === 'outputRules' ? (
              <EditorSectionCard
                title={t('contracts.templates.sections.outputRules')}
                actions={
                  <button className="av-primary-button" disabled={isSaving} type="button" onClick={() => void handleSaveTemplate()}>
                    {t('contracts.templates.actions.saveTemplate')}
                  </button>
                }
              >
                <div className="av-form-grid">
                  <label className="av-form-field">
                    <span>{t('contracts.templates.fields.numberingPrefix')}</span>
                    <TextField
                      value={draft.version.outputRules.numberingPrefix ?? ''}
                      onValueChange={(value) =>
                        updateVersionDraft({
                          outputRules: {
                            ...draft.version.outputRules,
                            numberingPrefix: value || null
                          }
                        })
                      }
                    />
                  </label>
                </div>
                <div className="av-form-field">
                  <span>{t('contracts.templates.fields.signatureFooter')}</span>
                  <TextAreaField
                    rows={4}
                    value={draft.version.outputRules.signatureFooter ?? ''}
                    onValueChange={(value) =>
                      updateVersionDraft({
                        outputRules: {
                          ...draft.version.outputRules,
                          signatureFooter: value || null
                        }
                      })
                    }
                  />
                </div>
                <div className="av-form-field">
                  <span>{t('contracts.templates.fields.changeSummary')}</span>
                  <TextAreaField
                    rows={3}
                    value={draft.version.changeSummary ?? ''}
                    onValueChange={(value) => updateVersionDraft({ changeSummary: value || null })}
                  />
                </div>
              </EditorSectionCard>
            ) : null}

            {editorTab === 'versions' ? (
              <EditorSectionCard title={t('contracts.templates.sections.versions')} actions={<OverflowMenuButton />}>
                {!detail || detail.versions.length === 0 ? (
                  <EmptyState title={t('contracts.templates.sections.versions')} description={t('contracts.templates.empty.versions')} />
                ) : (
                  <ul className="av-compact-list">
                    {detail.versions.map((version) => (
                      <li key={version.id} className="av-compact-list__item">
                        <div>
                          <strong>{t('contracts.templates.versions.versionLabel', { version: version.versionNumber })}</strong>
                          <span>{version.changeSummary ?? t('contracts.templates.versions.noChangeSummary')}</span>
                        </div>
                        <div className="av-inline-metadata">
                          {detail.template.currentVersionId === version.id ? (
                            <StatusBadge label={t('contracts.templates.versions.current')} tone="primary" />
                          ) : null}
                          <span>{new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(version.createdAt))}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </EditorSectionCard>
            ) : null}
          </>
        }
        rail={
          <>
            <EditorSectionCard
              title={t('contracts.templates.sections.generation')}
              actions={
                <div className="av-toolbar-group">
                  <button
                    className="av-secondary-button"
                    disabled={!detail?.currentVersion || !generationContext || !generationContext.customerId || isPreviewing}
                    type="button"
                    onClick={() => void handleRefreshPreview()}
                  >
                    {t('contracts.templates.actions.refreshPreview')}
                  </button>
                  <button
                    className="av-primary-button"
                    disabled={!detail?.currentVersion || !generationContext || !generationContext.customerId || isGenerating || dirty}
                    type="button"
                    onClick={() => void handleGenerateContract()}
                  >
                    {t('contracts.templates.actions.generateContract')}
                  </button>
                </div>
              }
            >
              {!detail?.currentVersion ? (
                <EmptyState title={t('contracts.templates.sections.generation')} description={t('contracts.templates.preview.saveRequired')} />
              ) : generationContext ? (
                <>
                  {dirty ? <div className="av-inline-error">{t('contracts.templates.messages.saveToPreview')}</div> : null}
                  {previewError ? <div className="av-inline-error">{previewError}</div> : null}

                  <div className="av-form-grid">
                    <label className="av-form-field">
                      <span>{t('contracts.templates.fields.customer')}</span>
                      <SelectField
                        options={customerOptions.map((customer) => ({ value: customer.id, label: customer.name }))}
                        value={generationContext.customerId}
                        onChange={(event) => {
                          const nextCustomerId = event.target.value;
                          const nextProjectId = detail.projectSummaries.find((project) => project.customerId === nextCustomerId)?.id ?? null;
                          updateGenerationContext({ customerId: nextCustomerId, projectId: nextProjectId });
                        }}
                      />
                    </label>
                    <label className="av-form-field">
                      <span>{t('contracts.templates.fields.project')}</span>
                      <SelectField
                        options={[
                          { value: '', label: t('common.notSet') },
                          ...projectOptions.map((project) => ({ value: project.id, label: project.name }))
                        ]}
                        value={generationContext.projectId ?? ''}
                        onChange={(event) => updateGenerationContext({ projectId: event.target.value || null })}
                      />
                    </label>
                    <label className="av-form-field">
                      <span>{t('contracts.templates.fields.issuer')}</span>
                      <SelectField
                        options={companyProfiles.map((profile) => ({ value: profile.id, label: profile.name }))}
                        value={generationContext.issuerCompanyProfileId ?? companyProfiles[0]?.id ?? ''}
                        onChange={(event) => updateGenerationContext({ issuerCompanyProfileId: event.target.value || null })}
                      />
                    </label>
                    <label className="av-form-field">
                      <span>{t('contracts.templates.fields.contractTitle')}</span>
                      <TextField
                        value={generationContext.contractValues.title ?? ''}
                        onValueChange={(value) => updateContractValues({ title: value || null })}
                      />
                    </label>
                    <label className="av-form-field">
                      <span>{t('contracts.templates.fields.effectiveFrom')}</span>
                      <TextField
                        type="date"
                        value={formatDateInputValue(generationContext.contractValues.effectiveFrom)}
                        onValueChange={(value) => updateContractValues({ effectiveFrom: toIsoDate(value) })}
                      />
                    </label>
                    <label className="av-form-field">
                      <span>{t('contracts.templates.fields.effectiveUntil')}</span>
                      <TextField
                        type="date"
                        value={formatDateInputValue(generationContext.contractValues.effectiveUntil)}
                        onValueChange={(value) => updateContractValues({ effectiveUntil: toIsoDate(value) })}
                      />
                    </label>
                    <label className="av-form-field">
                      <span>{t('contracts.templates.fields.totalValue')}</span>
                      <TextField
                        type="number"
                        value={generationContext.contractValues.totalValue == null ? '' : String(generationContext.contractValues.totalValue)}
                        onValueChange={(value) =>
                          updateContractValues({
                            totalValue: value ? Number(value) : null
                          })
                        }
                      />
                    </label>
                    <label className="av-form-field">
                      <span>{t('contracts.templates.fields.currency')}</span>
                      <TextField
                        value={generationContext.contractValues.currency ?? ''}
                        onValueChange={(value) => updateContractValues({ currency: value || null })}
                      />
                    </label>
                  </div>
                  <div className="av-form-field">
                    <span>{t('contracts.templates.fields.scopeSummary')}</span>
                    <TextAreaField
                      rows={3}
                      value={generationContext.contractValues.serviceScopeSummary ?? ''}
                      onValueChange={(value) => updateContractValues({ serviceScopeSummary: value || null })}
                    />
                  </div>
                  <div className="av-form-field">
                    <span>{t('contracts.templates.fields.paymentNotes')}</span>
                    <TextAreaField
                      rows={3}
                      value={generationContext.contractValues.paymentNotes ?? ''}
                      onValueChange={(value) => updateContractValues({ paymentNotes: value || null })}
                    />
                  </div>
                  <div className="av-form-field">
                    <span>{t('contracts.templates.fields.scheduleSummary')}</span>
                    <TextAreaField
                      rows={3}
                      value={generationContext.contractValues.scheduleSummary ?? ''}
                      onValueChange={(value) => updateContractValues({ scheduleSummary: value || null })}
                    />
                  </div>
                  <div className="av-form-field">
                    <span>{t('contracts.templates.fields.renewalNotes')}</span>
                    <TextAreaField
                      rows={2}
                      value={generationContext.contractValues.renewalNotes ?? ''}
                      onValueChange={(value) => updateContractValues({ renewalNotes: value || null })}
                    />
                  </div>
                  <div className="av-form-field">
                    <span>{t('contracts.templates.fields.terminationNotes')}</span>
                    <TextAreaField
                      rows={2}
                      value={generationContext.contractValues.terminationNotes ?? ''}
                      onValueChange={(value) => updateContractValues({ terminationNotes: value || null })}
                    />
                  </div>

                  {manualPlaceholders.length > 0 ? (
                    <div className="av-summary-stack">
                      <strong>{t('contracts.templates.sections.manualTokens')}</strong>
                      {manualPlaceholders.map((placeholder) => (
                        <label key={placeholder.key} className="av-form-field">
                          <span>{getPlaceholderLabel(placeholder, t)}</span>
                          <TextField
                            value={generationContext.manualTokenValues[placeholder.key] ?? ''}
                            onValueChange={(value) =>
                              setGenerationContext((current) =>
                                current
                                  ? {
                                      ...current,
                                      manualTokenValues: {
                                        ...current.manualTokenValues,
                                        [placeholder.key]: value
                                      }
                                    }
                                  : current
                              )
                            }
                          />
                        </label>
                      ))}
                    </div>
                  ) : null}
                </>
              ) : null}
            </EditorSectionCard>

            <EditorSectionCard title={t('contracts.templates.sections.preview')} actions={<OverflowMenuButton />}>
              {!detail?.currentVersion ? (
                <EmptyState title={t('contracts.templates.sections.preview')} description={t('contracts.templates.preview.saveRequired')} />
              ) : !preview ? (
                <EmptyState title={t('contracts.templates.sections.preview')} description={t('contracts.templates.preview.empty')} />
              ) : (
                <div className="av-summary-stack">
                  <ValuePair label={t('contracts.templates.preview.contractNumber')} value={preview.contractNumberPreview} />
                  <ValuePair label={t('contracts.templates.preview.contractTitle')} value={preview.contractTitlePreview} />
                  <ValuePair label={t('contracts.templates.preview.currency')} value={preview.currency} />
                  <ValuePair label={t('contracts.templates.preview.missingTokens')} value={preview.missingTokens.length} />

                  {preview.missingTokens.length > 0 ? (
                    <div className="av-readonly-text-block">{preview.missingTokens.join(', ')}</div>
                  ) : null}

                  <div className="av-customer-secondary-grid">
                    <div className="av-summary-stack">
                      <strong>{t('contracts.templates.preview.customerSnapshot')}</strong>
                      <span>{preview.customerSnapshot?.legalName ?? t('common.notSet')}</span>
                      <span>{formatSnapshotAddress(preview.customerSnapshot) ?? t('common.notSet')}</span>
                      <span>{formatRepresentativeName(preview.customerSnapshot) ?? t('common.notSet')}</span>
                    </div>
                    <div className="av-summary-stack">
                      <strong>{t('contracts.templates.preview.issuerSnapshot')}</strong>
                      <span>{preview.issuerSnapshot?.legalName ?? t('common.notSet')}</span>
                      <span>{formatSnapshotAddress(preview.issuerSnapshot) ?? t('common.notSet')}</span>
                      <span>{formatRepresentativeName(preview.issuerSnapshot) ?? t('common.notSet')}</span>
                    </div>
                  </div>

                  <div className="av-form-field">
                    <span>{t('contracts.templates.preview.body')}</span>
                    <div className="av-readonly-text-block">{preview.resolvedBodySource}</div>
                  </div>
                </div>
              )}
            </EditorSectionCard>
          </>
        }
      />
    </EditorPageLayout>
  );
}
